import React, { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, Send, Loader2, ChevronLeft, ChevronRight, RotateCcw, X } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

const EXAMPLE_QUESTIONS = [
  "What should I focus on today?",
  "Which discipline has the most overdue items?",
  "Who has the highest workload?",
  "What is the oldest item in the lab?",
  "Why has backlog increased this week?",
];

function sessionId() {
  let id = localStorage.getItem("pte_techdash_ai_session");
  if (!id) {
    id = `s${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("pte_techdash_ai_session", id);
  }
  return id;
}

// Pairs the flat user/assistant message log into one Q&A "turn" per
// question, so the user can step Back/Forward through past questions
// instead of scrolling an endless chat log.
function buildTurns(messages) {
  const turns = [];
  for (let i = 0; i < messages.length; i++) {
    if (messages[i].role === "user") {
      const next = messages[i + 1];
      turns.push({
        question: messages[i].content,
        answer: next?.role === "assistant" ? next.content : null,
        actions: next?.actions || [],
        pending: !(next?.role === "assistant"),
      });
      if (next?.role === "assistant") i++;
    }
  }
  return turns;
}

// Read-only AI Technical Manager copilot (Claude Sonnet 4.6) — grounded in
// live PTE data computed on the backend. Cannot write to the database;
// answers include suggested dashboard filters the user can click to apply.
// Shows one Q&A "turn" at a time with Back/Forward navigation, plus an
// always-available input to ask more questions.
export function AiCopilotPanel({ onApplyFilters }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [viewIndex, setViewIndex] = useState(0);
  const sid = useRef(sessionId());

  const turns = useMemo(() => buildTurns(messages), [messages]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/technical-dashboard/ai/history", { params: { session_id: sid.current } });
        setMessages(data || []);
      } catch (e) {
        // silent — history is a convenience, not critical
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, []);

  useEffect(() => {
    // Whenever a new turn appears (question sent / answer arrives), jump to it.
    setViewIndex(Math.max(0, turns.length - 1));
  }, [turns.length]);

  async function send(text) {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/technical-dashboard/ai/ask", { session_id: sid.current, message: q });
      setMessages((m) => [...m, { role: "assistant", content: data.answer, actions: data.actions || [], insufficient_data: data.insufficient_data }]);
    } catch (e) {
      toast.error(apiErr(e));
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't process that just now.", actions: [] }]);
    } finally {
      setBusy(false);
    }
  }

  function startNewConversation() {
    const id = `s${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("pte_techdash_ai_session", id);
    sid.current = id;
    setMessages([]);
    setViewIndex(0);
  }

  const current = turns[viewIndex];
  const atLatest = viewIndex >= turns.length - 1;
  const atEarliest = viewIndex <= 0;

  return (
    <>
      <button
        type="button"
        className={`td-ai-fab ${open ? "open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        data-testid="ai-copilot-fab"
        title="AI Technical Manager Copilot"
      >
        <Sparkles size={17} />
        <span className="td-ai-fab-label">AI Copilot</span>
      </button>

      {open && (
        <div className="td-ai-popup" data-testid="ai-copilot-panel">
          <div className="sec-h" style={{ marginBottom: 10 }}>
            <h3><Sparkles size={16} style={{ verticalAlign: -2, marginRight: 6, color: "var(--gold-deep)" }} />AI Technical Manager Copilot</h3>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span className="tag-soft">Read-only</span>
              {turns.length > 0 && (
                <button className="btn ghost sm" data-testid="ai-copilot-new-chat" title="Start a new conversation" onClick={startNewConversation}>
                  <RotateCcw size={12} />
                </button>
              )}
              <button className="btn ghost sm" data-testid="ai-copilot-close" title="Close" onClick={() => setOpen(false)}>
                <X size={13} />
              </button>
            </div>
          </div>

          <div className="td-ai-messages" data-testid="ai-copilot-messages">
            {loadingHistory ? (
              <div className="t-mut" style={{ fontSize: 12.5 }}>Loading…</div>
            ) : turns.length === 0 ? (
              <div className="td-ai-empty">
                <div className="t-mut" style={{ fontSize: 12.5, marginBottom: 8 }}>Ask me about workload, backlog, bottlenecks, or overdue work.</div>
                {EXAMPLE_QUESTIONS.map((q) => (
                  <button key={q} className="td-ai-example" onClick={() => send(q)} data-testid={`ai-example-${q.slice(0, 10).replace(/\s/g, "-")}`}>
                    {q}
                  </button>
                ))}
              </div>
            ) : (
              <div data-testid={`ai-turn-${viewIndex}`}>
                <div className="td-ai-msg user">
                  <div className="td-ai-bubble">{current.question}</div>
                </div>
                <div className="td-ai-msg assistant">
                  {current.pending ? (
                    <div className="td-ai-bubble td-ai-thinking"><Loader2 size={13} className="spin" /> Thinking…</div>
                  ) : (
                    <>
                      <div className="td-ai-bubble">{current.answer}</div>
                      {!!(current.actions || []).length && (
                        <div className="td-ai-actions">
                          {current.actions.map((a, ai) => (
                            <button
                              key={ai}
                              className="td-ai-action-btn"
                              data-testid={`ai-action-${viewIndex}-${ai}`}
                              onClick={() => onApplyFilters(a.filters || {})}
                            >
                              {a.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {turns.length > 0 && (
            <div className="td-ai-nav" data-testid="ai-copilot-nav">
              <button
                className="btn ghost sm" data-testid="ai-copilot-back"
                disabled={atEarliest} onClick={() => setViewIndex((i) => Math.max(0, i - 1))}
              >
                <ChevronLeft size={13} /> Back
              </button>
              <span className="t-mut" style={{ fontSize: 11.5 }} data-testid="ai-copilot-turn-counter">
                Question {viewIndex + 1} of {turns.length}
              </span>
              <button
                className="btn ghost sm" data-testid="ai-copilot-forward"
                disabled={atLatest} onClick={() => setViewIndex((i) => Math.min(turns.length - 1, i + 1))}
              >
                Forward <ChevronRight size={13} />
              </button>
            </div>
          )}

          <form
            className="td-ai-input-row"
            onSubmit={(e) => { e.preventDefault(); send(); }}
          >
            <input
              className="input"
              placeholder="Ask about workload, backlog, overdue items…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="ai-copilot-input"
              disabled={busy}
            />
            <button type="submit" className="btn gold sm" disabled={busy || !input.trim()} data-testid="ai-copilot-send">
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
